import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import {
  Button,
  MenuItem,
  Modal,
  Paper,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import PolicyIcon from '@material-ui/icons/Policy'
import { useState } from 'react'

import { getDirection, getTranslate } from '../../localization'
import {
  CancelPolicy,
  PolicyTable,
  PolicyPenaltyTypes,
  PolicyType
} from '../../types/cancelAndModifyPolicies'
import useStyles from './styles/PoliciesFormAddPoliciesModal.styles'

const PENALTY_TYPE_OPTIONS: { value: PolicyPenaltyTypes; label: string }[] = [
  { value: 'TotalStay', label: 'Total Stay' },
  { value: 'FirstNight', label: 'First Night' }
]

interface PoliciesFormAddPoliciesModalProps {
  isOpen: boolean
  onClose: () => void
  values: Pick<CancelPolicy, 'table'>
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  // The penalty types a rule may use; add-on policies allow only TotalStay.
  penaltyTypes?: PolicyPenaltyTypes[]
}
const PoliciesFormAddPoliciesModal: React.FC<
  PoliciesFormAddPoliciesModalProps
> = ({
  isOpen,
  onClose,
  values,
  setFieldValue,
  penaltyTypes = ['TotalStay', 'FirstNight']
}) => {
  const theme = useTheme()
  const classes = useStyles()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [policy, setpolicy] = useState<PolicyTable>({
    deadline: 0,
    deadlineTimeOffset: '00:00',
    penaltyAmount: 0,
    penaltyType: penaltyTypes.includes('FirstNight')
      ? 'FirstNight'
      : penaltyTypes[0],
    priority: 1,
    policyType: 'BeforeCheckInTime'
  })

  const convertToPolicyPenaltyTypes = (str: string) => {
    let pp: PolicyPenaltyTypes = 'FirstNight'
    switch (str) {
      case 'FirstNight':
        pp = 'FirstNight'
        break
      case 'TotalStay':
        pp = 'TotalStay'
        break

      default:
        break
    }
    return pp
  }
  const convertToPolicyTypes = (str: string) => {
    let pp: PolicyType = 'BeforeCheckInTime'
    switch (str) {
      case 'BeforeCheckInTime':
        pp = 'BeforeCheckInTime'
        break
      case 'AfterReservationTime':
        pp = 'AfterReservationTime'
        break
      case 'AfterCheckInTime':
        pp = 'AfterCheckInTime'
        break
      case 'BeforeCheckOutTime':
        pp = 'BeforeCheckOutTime'
        break

      default:
        break
    }
    return pp
  }

  const addPolicy = (p: PolicyTable) => {
    setFieldValue('table', [...values.table, p])
  }

  return (
    <Modal
      dir={getDirection()}
      open={isOpen}
      onClose={onClose}
      className={classes.modal}>
      <Slide in={isOpen} direction="down">
        <Paper className={classes.paper}>
          <PolicyIcon className={classes.policyIcon} />
          <div className={classes.header}>
            <Typography variant="h3">
              {getTranslate('Create New Policy')}
            </Typography>
          </div>
          <div className={classes.inputContainer}>
            <InputWithLabel
              inputSize="small"
              label={getTranslate('Priority')}
              value={policy?.priority}
              type="number"
              onChange={(event) =>
                setpolicy({
                  ...policy,
                  priority: parseInt(event.currentTarget.value)
                })
              }
            />
            <InputWithLabel
              inputSize="small"
              label={getTranslate('Penalty Amount')}
              value={policy?.penaltyAmount}
              type="number"
              onChange={(event) =>
                setpolicy({
                  ...policy,
                  penaltyAmount: Math.min(
                    parseInt(event.currentTarget.value),
                    100
                  )
                })
              }
              endIcon={<div>%</div>}
            />
            <SelectWithLabel
              label={getTranslate('Penalty Type')}
              size="small"
              value={policy.penaltyType}
              onChange={(event) =>
                setpolicy({
                  ...policy,
                  penaltyType: convertToPolicyPenaltyTypes(event.target.value)
                })
              }>
              {PENALTY_TYPE_OPTIONS.filter((option) =>
                penaltyTypes.includes(option.value)
              ).map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {getTranslate(option.label)}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <SelectWithLabel
              label={getTranslate('Type')}
              size="small"
              value={policy.policyType}
              onChange={(event) =>
                setpolicy({
                  ...policy,
                  policyType: convertToPolicyTypes(event.target.value)
                })
              }>
              <MenuItem key="Before Checkin Time" value="BeforeCheckInTime">
                {getTranslate('Before Checkin Time')}
              </MenuItem>
              <MenuItem
                key="After Reservation Time"
                value="AfterReservationTime">
                {getTranslate('After Reservation Time')}
              </MenuItem>
              <MenuItem key="After Checkin Time" value="AfterCheckInTime">
                {getTranslate('After Checkin Time')}
              </MenuItem>
              <MenuItem key="Before Checkout Time" value="BeforeCheckoutTime">
                {getTranslate('Before Checkout Time')}
              </MenuItem>
            </SelectWithLabel>
            <InputWithLabel
              type="number"
              inputSize="small"
              label={getTranslate('Hours Deadline')}
              value={policy?.deadline}
              onChange={(event) =>
                setpolicy({
                  ...policy,
                  deadline: parseInt(event.currentTarget.value)
                })
              }
            />
            <InputWithLabel
              placeholder={getTranslate('00:00')}
              inputSize="small"
              label={getTranslate('Deadline Time Offset')}
              value={policy?.deadlineTimeOffset}
              onChange={(event) =>
                setpolicy({
                  ...policy,
                  deadlineTimeOffset: event.currentTarget.value
                })
              }
            />
          </div>
          <div className={classes.button}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth={isSmallScreen}
              onClick={() => {
                onClose()
              }}>
              {getTranslate('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth={isSmallScreen}
              onClick={() => {
                addPolicy(policy)
                onClose()
              }}>
              {getTranslate('Save')}
            </Button>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}
export default PoliciesFormAddPoliciesModal
